import { CheckoutInvoice } from '@/types/Invoice';
import { createSelector } from '@reduxjs/toolkit';
import { getAuthToken, getBidderId } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import api from '@/redux/api/checkout';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_INVOICE_FAIL = 'la/ui/checkout/LOAD_FAIL';
export const LOAD_INVOICE_REQUEST = 'la/ui/checkout/LOAD_REQUEST';
export const LOAD_INVOICE_SUCCESS = 'la/ui/checkout/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
export const DEFAULT_STATE = {
    byId: {},
    error: {},
    loaded: {},
    loading: [],
};

export type State = {
    byId: { [catalogId: number]: CheckoutInvoice };
    error: { [catalogId: number]: any };
    loaded: { [catalogId: number]: boolean };
    loading: number[];
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_INVOICE_FAIL:
            return {
                ...state,
                error: { ...state.error, [action.meta.catalogId]: action.payload },
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_INVOICE_REQUEST:
            return {
                ...state,
                error: { ...state.error, [action.payload]: undefined },
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_INVOICE_SUCCESS:
            time = action.meta.actionTime;
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded, [action.meta.catalogId]: time };
            loading = difference(loading, [action.meta.catalogId]);
            existing[action.meta.catalogId] = { ...action.payload.data };
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.checkout;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

const errorSelector = createSelector(stateSelector, (state) => state.error);

export const getInvoice: (state: any, selector: any) => CheckoutInvoice = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || { invoice: {}, items: [], shippingAddress: {} }
);

const getLoadTimeForInvoice = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);
export const isInvoiceLoaded = getLoadTimeForInvoice;
const isInvoiceLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));
export const getInvoiceErrors = createSelector([errorSelector, idSelector], (errors, catalogId) => {
    let error = errors[catalogId];
    if (error) {
        if (error.status === 500) {
            return 'Error loading invoice, please refresh and try again';
        }
    }
    return undefined;
});

const shouldFetchInvoice = createSelector(
    [idSelector, getInvoice, getLoadTimeForInvoice, isInvoiceLoading],
    (catalogId, invoice, loaded, loading) => {
        if (!catalogId) {
            return false;
        }
        if (!isEmpty(invoice)) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) {
                return false;
            }
        }
        return !loading;
    }
);

/* ACTION CREATORS */
export const fetchInvoiceIfNeeded =
    (catalogId: number, paymentProvider: number, force?: boolean) => async (dispatch: Function, getState: Function) => {
        const state = getState();
        if (force || shouldFetchInvoice(state, catalogId)) {
            try {
                const authToken = getAuthToken(state);
                const deployment = getDeployment(state);
                const bidderId = getBidderId(state);
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: catalogId,
                    type: LOAD_INVOICE_REQUEST,
                });
                const response = await api.fetchInvoice({ authToken, bidderId, catalogId, deployment });
                dispatch({
                    meta: { actionTime: Date.now(), catalogId },
                    payload: { data: response.data, paymentProvider },
                    type: LOAD_INVOICE_SUCCESS,
                });
            } catch (error) {
                dispatch({
                    error: true,
                    meta: { catalogId },
                    payload: error,
                    type: LOAD_INVOICE_FAIL,
                });
            }
        } else {
            return Promise.resolve();
        }
    };
